import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import FlexService from '../../services/FlexService';

// A type-to-search field: its options script is asked again as the user types, with { search, limit }, so a list
// longer than one page (suppliers, items, locations) is searched on the server instead of only the first batch.
// The text typed is kept as the value when nothing is picked, as in the create form (EntityWizardOverlay), so a
// saved value that isn't among the matches still shows. Each option is { value, label }.
const SEARCH_LIMIT = 20;

export default function SearchableOptionsField({ field, value, onChange, disabled, error, rowData, entityName, sx }) {
  const [options, setOptions] = useState([]);
  const debounce = useRef(null);

  const search = useCallback((text) => {
    if (!field.optionsScript) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const response = await FlexService.executeScript({
          scriptName: field.optionsScript,
          context: { _payload: { ...(rowData || {}), search: text || '', limit: SEARCH_LIMIT, _field: field.fieldName, _entity: entityName } },
        });
        const ctx = response?.context || {};
        const opts = ctx.result || ctx.data || [];
        if (response?.success && Array.isArray(opts)) setOptions(opts.filter((o) => !(o && typeof o === 'object' && o.info)));
      } catch (err) {
        console.error(`Search failed for ${field.fieldName}:`, err);
      }
    }, 300);
  }, [field.optionsScript, field.fieldName, rowData, entityName]);

  useEffect(() => () => debounce.current && clearTimeout(debounce.current), []);

  const normalized = options.map((o) => (typeof o === 'object'
    ? { value: o.value ?? '', label: o.label || o.value || '' }
    : { value: String(o), label: String(o) }));
  const current = normalized.find((o) => o.value === value) || null;

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={normalized}
      value={current || (value || '')}
      filterOptions={(opts) => opts} // the server already matched them
      getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.label || o?.value || ''))}
      isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o : o?.value) === (typeof v === 'string' ? v : v?.value)}
      renderOption={(props, o) => <li {...props} key={o.value}>{o.label || o.value}</li>}
      onChange={(_, picked) => onChange(field.fieldName, picked == null ? '' : (typeof picked === 'string' ? picked : picked.value))}
      onInputChange={(_, text, reason) => {
        if (reason === 'input') {
          onChange(field.fieldName, text);
          search(text);
        }
      }}
      onOpen={() => { if (options.length === 0) search(typeof value === 'string' ? value : ''); }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          required={!!field.isRequired}
          label={field.displayLabel}
          placeholder={field.placeholder || 'Type to search'}
          error={!!error}
          helperText={error || field.description}
          sx={sx}
        />
      )}
    />
  );
}
